<template>
  <div class="brands-showcase">
    <DraggableScroller
      class="brands-showcase__scroller"
      has-pagination
      has-arrow
      has-arrow-desktop
      show-single-active-pagination
      :autoplay-time="3000"
      :autoplay-elements="1"
    >
      <component
        :is="brand.slug ? 'nuxt-link' : 'span'"
        v-for="(brand, index) in mappedBrands"
        :key="index"
        :to="`smart-home/_${brand.slug}`"
        class="brands-showcase__logo"
        :aria-label="`${brand.name || 'Brand'} Logo`"
      >
        <CdnImage
          :alt="`${brand.name} Logo` || ''"
          :src="brand.logo || ''"
          class="brands-showcase__image"
          :class="`brands-showcase__image--${brand.slug}`"
          :image-style="`aspect-ratio: ${brand.aspectRatio}`"
          render-without-figure
        />
      </component>
    </DraggableScroller>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { addLogo } from '@/lib/brand';
import CdnImage from '@/components/cdn-image.vue';
import type { BrandAggregationQuery } from '~/lib/goliath/goliath.types';
import DraggableScroller from '~/components/draggable-scroller.vue';

type Brands = NonNullable<NonNullable<BrandAggregationQuery['offers']>['aggregations']>['brands'];

const props = defineProps({
  customSpeed: {
    type: String,
    default: null,
  },
  brands: {
    type: Array as PropType<Brands>,
    default: () => [],
  },
});

const speed = computed(() => props.customSpeed || `${Math.max(10, props.brands?.length || 1)}s`);

const mappedBrands = computed(() =>
  props.brands?.map((item) => (item ? addLogo(item.brand, true) : undefined)).filter(Boolean)
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.brands-showcase {
  width: 100%;
  overflow: hidden;
  margin-top: $grid-base;
  margin-bottom: 6 * $grid-base;

  &__scroller {
    :deep(.draggable-scroller__container) {
      display: flex;
      width: 100%;
      padding: $grid-base;
      grid-gap: $grid-base * 2;

      > * {
        flex: 0 0 167px;
        padding: $grid-base $grid-base * 2;
      }
    }
  }

  &__logo {
    height: 60px;
    border: 1px solid rgb(220, 220, 220);
    border-radius: 9px;
    padding: $grid-base $grid-base * 2;
    width: 168px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__image {
    height: 34px;
    width: 118px;
    object-fit: contain;
  }
}
</style>
